import { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { geneFilterOptions } from '../helpers';
import { AnalysisMethod, CombinedValidation, GseaParams } from '../types';

type EnrichmentParams = {
  commonVariants: number;
  commonGenes: number;
  maxPvalue: number;
  maxScore: number;
  minScore: number;
  source: string;
};

type EnrichmentRunDetailsProps = {
  analysisMethod: AnalysisMethod;
  searchType: string;
  geneFilterType: string;
  itemCount: number;
  validation?: CombinedValidation | null;
  notFoundItems?: string[];
  enrichmentParams: EnrichmentParams;
  gseaParams: GseaParams;
  showOra: boolean;
  showDsea: boolean;
};

function getIdTypeLabel(searchType: string, geneFilterType: string): string {
  if (searchType === 'VARIANTS') return 'rs number';
  return geneFilterOptions.find(option => option.value === geneFilterType)?.label ?? geneFilterType;
}

function getExponentLabel(exponent: number): string {
  if (exponent === 0) return '0 — Classic (unweighted)';
  if (exponent === 1) return '1 — Standard weighted';
  return String(exponent);
}

export default function EnrichmentRunDetails({
  analysisMethod,
  searchType,
  geneFilterType,
  itemCount,
  validation,
  notFoundItems = [],
  enrichmentParams,
  gseaParams,
  showOra,
  showDsea,
}: EnrichmentRunDetailsProps) {
  const [isOpen, setIsOpen] = useState(false);

  const idTypeLabel = getIdTypeLabel(searchType, geneFilterType);
  const entityLabel = searchType === 'GENES' ? 'genes' : 'variants';
  const invalidCount = validation?.invalidItems?.length ?? 0;
  const validCount = validation?.validItems;
  const unmappedCount = notFoundItems.length;

  return (
    <div className="mt-4 border-t border-gray-100 pt-3">
      <button
        type="button"
        id="enrichment_run_details_toggle"
        onClick={() => setIsOpen(open => !open)}
        className="flex w-full items-center justify-between gap-2 text-left"
        aria-expanded={isOpen}
      >
        <span className="text-sm font-medium text-gray-700">Analysis details</span>
        <ChevronDownIcon
          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="mt-3 space-y-4 text-sm text-gray-700">
          <div>
            <p>
              Input list: <strong>{itemCount}</strong> {entityLabel}. ID type: <strong>{idTypeLabel}</strong>.
            </p>
            {typeof validCount === 'number' && (
              <p className="mt-1">
                Of those, <strong>{validCount}</strong> had a valid identifier format
                {invalidCount > 0 ? (
                  <>
                    {' '}
                    and <strong>{invalidCount}</strong> could not be validated
                  </>
                ) : null}
                .
              </p>
            )}
            {unmappedCount > 0 && (
              <p className="mt-1">
                <strong>{unmappedCount}</strong> {entityLabel} were not found in DISGENET and were excluded
                from the analysis.
              </p>
            )}
            {analysisMethod === 'BOTH' && (
              <p className="mt-1 text-xs text-gray-500">
                ORA and DSEA were run on the same input list for side-by-side comparison.
              </p>
            )}
          </div>

          {showOra && (
            <div>
              <p className="font-semibold text-gray-900 mb-1">Parameters for the ORA analysis:</p>
              <ul className="list-disc pl-5 space-y-0.5">
                <li>
                  Source: <strong>{enrichmentParams.source}</strong>
                </li>
                <li>
                  Score range:{' '}
                  <strong>
                    {enrichmentParams.minScore} – {enrichmentParams.maxScore}
                  </strong>
                </li>
                <li>
                  Minimum {searchType === 'GENES' ? 'common genes' : 'common variants'}:{' '}
                  <strong>
                    {searchType === 'GENES' ? enrichmentParams.commonGenes : enrichmentParams.commonVariants}
                  </strong>
                </li>
                <li>
                  Maximum p-value: <strong>{enrichmentParams.maxPvalue}</strong>
                </li>
              </ul>
            </div>
          )}

          {showDsea && (
            <div>
              <p className="font-semibold text-gray-900 mb-1">Parameters for the DSEA analysis:</p>
              <ul className="list-disc pl-5 space-y-0.5">
                <li>
                  Source: <strong>{gseaParams.source}</strong>
                </li>
                <li>
                  Score range:{' '}
                  <strong>
                    {gseaParams.minScore} – {gseaParams.maxScore}
                  </strong>
                </li>
                <li>
                  Weighting exponent: <strong>{getExponentLabel(gseaParams.exponent)}</strong>
                </li>
                <li>
                  Minimum common genes: <strong>{gseaParams.minCommonGenes}</strong>
                </li>
                <li>
                  Minimum number of genes in the disease set: <strong>{gseaParams.minGeneSetSize}</strong>
                </li>
                <li>
                  Maximum number of genes in the disease set: <strong>{gseaParams.maxGeneSetSize}</strong>
                </li>
                <li>
                  Minimum intersection size: <strong>{gseaParams.minHits}</strong>
                </li>
                <li>
                  Number of permutations: <strong>{gseaParams.nPermutations}</strong>
                </li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
